import { HttpInterceptorFn, HttpErrorResponse, HttpRequest, HttpHandlerFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, catchError, filter, switchMap, take, throwError } from 'rxjs';
import { AuthService } from './auth.service';

/**
 * Endpoints donde NO se debe adjuntar/refrescar token.
 */
const PUBLIC_ENDPOINTS = ['/auth/login', '/auth/refresh', '/auth/forgot-password'];

/**
 * Estado compartido para deduplicar refresh concurrentes:
 * - Si llegan N peticiones con token expirado a la vez, solo se dispara UN refresh
 *   y las demás esperan al nuevo token en lugar de pegar al backend N veces.
 */
let isRefreshing = false;
const refreshSubject = new BehaviorSubject<string | null>(null);

function isPublic(url: string): boolean {
  return PUBLIC_ENDPOINTS.some(p => url.includes(p));
}

function attachToken(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  // Nota: NO añadir cabeceras como `Cache-Control` aquí. Cualquier cabecera
  // no "CORS-safelisted" obliga al navegador a hacer un preflight OPTIONS.
  // Si quieres no cachear respuestas autenticadas, hazlo desde el backend
  // con `Cache-Control: no-store` en la RESPUESTA.
  return req.clone({
    headers: req.headers.set('Authorization', `Bearer ${token}`)
  });
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  if (isPublic(req.url)) {
    return next(req);
  }

  const token = auth.getToken();
  if (!token) {
    return next(req);
  }

  // Si el token está expirado, intenta refresh antes de enviar la petición
  if (auth.isTokenExpired()) {
    return handleRefresh(req, next, auth, router);
  }

  return next(attachToken(req, token)).pipe(
    catchError((err: HttpErrorResponse) => {
      // 401 -> el servidor rechaza el token; intentar refresh una vez
      if (err.status === 401) {
        return handleRefresh(req, next, auth, router);
      }
      return throwError(() => err);
    })
  );
};

function handleRefresh(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  auth: AuthService,
  router: Router
): Observable<any> {
  if (isRefreshing) {
    // Esperar al token emitido por el refresh en curso
    return refreshSubject.pipe(
      filter((t): t is string => t !== null),
      take(1),
      switchMap(t => next(attachToken(req, t)))
    );
  }

  isRefreshing = true;
  refreshSubject.next(null);

  return auth.refreshToken().pipe(
    switchMap(() => {
      const newToken = auth.getToken();
      isRefreshing = false;
      refreshSubject.next(newToken);
      return next(attachToken(req, newToken as string));
    }),
    catchError((err) => {
      isRefreshing = false;
      auth.forceLogout();
      router.navigate(['/login']);
      return throwError(() => err);
    })
  );
}
